import type { IInboxWireItem } from '@platform/dao';
import { recordNumber } from '@platform/engine/views/recordNumber';
import type { IInboxEntry } from './IInboxEntry';
import type { InboxKind } from './InboxKind';

const KINDS: readonly InboxKind[] = ['assignment', 'reminder', 'comment', 'update'];
/** Початок тексту SlaMonitor (слово, далі двокрапка чи пробіл; \b не працює з кирилицею без прапорця u). */
const OVERDUE = /^(прострочено|overdue)(?=[\s:]|$)/iu;
const DUE = /^(наближається термін|строк|due)(?=[\s:]|$)/iu;

/**
 * Вид елемента: новий контракт надсилає assignment | reminder | comment | update; старий — notification, текст якого формує сервер
 * (SlaMonitor: «Прострочено: …», «Наближається термін: …»; CommentWriter; призначення) — тоді вид визначається за текстом.
 */
function kindOf(item: IInboxWireItem): InboxKind {
  if (KINDS.includes(item.kind as InboxKind)) return item.kind as InboxKind;
  const text = item.text ?? '';
  if (OVERDUE.test(text) || DUE.test(text)) return 'reminder';
  if (/коментар|прокоментува|commented/i.test(text)) return 'comment';
  if (/призначено|призначив|assigned/i.test(text)) return 'assignment';
  return 'update';
}

/**
 * Нормалізація відповіді GET /me/inbox (чиста функція): плоскі поля запису старого формату або конверт `record` нового; прострочення
 * нагадування — з поля late, інакше за терміном (раніше за `today`) чи текстом «Прострочено…»; коментарі відкривають вкладку коментарів.
 * Найновіші — першими.
 */
export function normalizeInbox(items: readonly IInboxWireItem[], today: string): IInboxEntry[] {
  return items.map((item): IInboxEntry => {
    const kind = kindOf(item);
    const rec = item.record ?? null;
    const due = (item.due ?? rec?.due ?? null)?.slice(0, 10) ?? null;
    const text = item.text ?? '';
    const late = kind === 'reminder' && (item.late ?? ((!!due && due < today) || OVERDUE.test(text)));
    return {
      key: `${item.kind}:${item.id}`, id: item.id, kind, text,
      workspaceId: item.workspaceId ?? rec?.workspaceId ?? null, workspaceName: item.workspaceName ?? null,
      recordId: rec?.id ?? rec?.recordId ?? item.recordId ?? null, recordType: rec?.recordType ?? item.recordType ?? null,
      module: rec?.module ?? item.module ?? null, display: rec?.display ?? null, title: rec?.title ?? null,
      number: rec?.display ? recordNumber({ code: rec.code ?? 0, display: rec.display }) : null,
      due, late, tab: item.tab ?? (kind === 'comment' ? 'comments' : null), at: item.at, read: item.read,
      actor: item.actor?.name ?? null, excerpt: item.excerpt ?? null, stored: item.stored ?? true,
    };
  }).sort((a, b) => b.at.localeCompare(a.at));
}
